import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    input,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import clone from 'lodash-es/clone';
import isFunction from 'lodash-es/isFunction';
import noop from 'lodash-es/noop';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, Observable, Subscription, tap, throttleTime } from 'rxjs';
import { MultiInputControlModalComponent } from 'shared/components/multi-input-control/multi-input-control-modal/multi-input-control-modal.component';
import { isElementFullyVisible } from 'shared/functions/is-element-fully-visible.function';
import { ListItem } from 'shared/interfaces/list-item';
import { ModalService } from 'shared/services';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';

const OPEN_CLOSE_COOLDOWN: number = 250;

@Directive()
export abstract class BaseMultiControl implements ControlValueAccessor, OnInit, OnDestroy {
    // Set touched on blur
    @HostListener('blur')
    public onTouched() {
        this.onTouchFunction();
    }

    @ViewChild('dropdownMenu', { read: ElementRef }) dropdownMenu!: ElementRef;
    @ViewChildren('menuItemAuto') optionsChildren!: QueryList<ElementRef>;
    @ViewChildren('multiSelectInput') multiSelectInput!: QueryList<ElementRef>;

    /**
     * Provide either an array of objects or a URL from which to get the options
     */
    @Input()
    public set optionsList(value: any[]) {
        this._optionsList = value;
        this.computeOptions();
        this.addSelectionsFromQueue();
    }

    public get optionsList() {
        return this._optionsList;
    }

    @Input({ required: true })
    public set idKey(value: string) {
        this._idKey = value;
        this.computeOptions();
    }

    public get idKey() {
        return this._idKey;
    }

    @Input({ required: true })
    public set labelKey(value: string) {
        this._labelKey = value;
        this.computeOptions();
    }

    public get labelKey() {
        return this._labelKey;
    }

    @Input()
    public set dynamicOptions(value: { getDataFunction: Function; getDataFunctionParams?: any } | undefined) {
        this._dynamicOptions = value;
        this.computeOptions();
    }

    public get dynamicOptions(): { getDataFunction: Function; getDataFunctionParams?: any } | undefined {
        return this._dynamicOptions;
    }

    @Input() disabled!: boolean;

    @Input() maximumSelections: number = 0;

    @Output() change = new EventEmitter();

    enforceOneValue = input<boolean>(false);

    protected _optionsList: ListItem[] = [];
    protected _idKey: string = '';
    protected _labelKey: string = '';
    protected _dynamicOptions?: { getDataFunction: Function; getDataFunctionParams?: any };
    protected internalOptionsList: ListItem[] = [];
    protected filteredOptionsList: ListItem[] = [];
    protected externalOptionsQueue?: string[];
    protected showDropdownCurrentValue?: boolean;

    public isDisabled = false;
    public dropdownTopPositioning?: boolean = false;
    public filteredAvailableOptionsList: ListItem[] = [];
    public selectedOptions: ListItem[] = [];
    public selectedIndex = -1;
    public showDropdownSubject = new BehaviorSubject(false);
    public showDropdown = this.showDropdownSubject.pipe(
        throttleTime(OPEN_CLOSE_COOLDOWN),
        tap((newValue: boolean) => (this.showDropdownCurrentValue = newValue)),
    );

    protected onChangeFunction: (_: any) => void = noop;
    protected onTouchFunction: () => void = noop;

    public form = new UntypedFormGroup({
        searchInput: new UntypedFormControl(null),
    });

    protected subscriptions: { [key: string]: Subscription | undefined } = {};

    constructor(
        protected modalService: ModalService,
        protected changeDetectorRef: ChangeDetectorRef,
        protected translate: TranslateService,
    ) {}

    public ngOnInit() {
        this.setDisabledState(this.disabled);
        this.subscriptions.dynamicValueChanges = this.form
            .get('searchInput')
            ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
            .subscribe((value: string) => {
                if (this.areDynamicOptionsValid()) {
                    const dataFunctionResult = this.dynamicOptions?.getDataFunction(
                        value,
                        ...(this.dynamicOptions?.getDataFunctionParams ?? []),
                    );

                    if (dataFunctionResult instanceof Observable) {
                        dataFunctionResult.subscribe((data) => {
                            this.createOptionsList(data);
                            this.addSelectionsFromQueue();
                            // this.changeDetectorRef.detectChanges();
                        });
                    }
                }
            });

        this.subscriptions.valueChanges = combineLatest([
            this.translate.stream('init'),
            this.form.get('searchInput')!.valueChanges,
        ]).subscribe(([languageLoaded, value]) => {
            if (!this.areDynamicOptionsValid()) {
                //Filter options using partial match
                this.showDropdownSubject.next(value !== undefined);

                this.filteredOptionsList = (this.internalOptionsList as any[]).filter((option) =>
                    this.translate
                        .instant(option.label)
                        .toString()
                        .toLowerCase()
                        .includes((value ?? '').toLowerCase()),
                );

                //Exclude the already selected options from the filtered list
                this.updateAvailableOptionsList();
            }
        });
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public writeValue(values: string[]): void {
        if (!Array.isArray(values) || !values.length) {
            if (this.selectedOptions.length > 0) {
                this.deselectAll();
            }
        } else {
            if (!this.internalOptionsList) {
                this.subscriptions.getOptions?.add(() => {
                    this.handleExternalValueChanges(values);
                });
            } else {
                this.handleExternalValueChanges(values);
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    public registerOnChange(fn: any): void {
        this.onChangeFunction = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouchFunction = fn;
    }

    public setDisabledState(isDisabled: boolean) {
        this.isDisabled = isDisabled;
        this.changeDetectorRef.markForCheck();

        if (isDisabled) {
            this.form.controls['searchInput']?.disable();
        } else {
            this.form.controls['searchInput']?.enable({ emitEvent: false });
        }
    }

    private computeOptions() {
        if (!(this.optionsList || this.areDynamicOptionsValid()) || !this.idKey || !this.labelKey) {
            this.internalOptionsList = [];
            return;
        }

        const value = this.optionsList ?? [];

        if (this.areDynamicOptionsValid()) {
            //TODO: Everytime search text changes, call dynamicOptions again
            const dataFunctionResult = this.dynamicOptions!.getDataFunction(
                this.form.get('searchInput')?.value,
                ...(this.dynamicOptions!.getDataFunctionParams ?? []),
            );

            if (dataFunctionResult instanceof Observable) {
                dataFunctionResult.subscribe((data) => {
                    this.createOptionsList(data);
                    this.addSelectionsFromQueue();
                    this.changeDetectorRef.detectChanges();
                });
            }
        }

        this.createOptionsList(value);
    }

    protected abstract selectOption(option: ListItem, emitEvent: boolean): void;

    public deselectAll() {
        const searchTerm = this.form.get('searchInput')?.value;
        this.selectedOptions = [];
        this.updateAvailableOptionsList();
        // Deselect all but take into consideration the existing filter text
        if (searchTerm) {
            // Filter options using partial match
            this.filteredOptionsList = this.internalOptionsList.filter((option) =>
                option.label.toString().toLowerCase().includes(searchTerm?.toLowerCase()),
            );
            // Exclude the already selected options from the filtered list
            this.updateAvailableOptionsList();
        } else {
            this.filteredOptionsList = this.internalOptionsList.slice();
            this.filteredAvailableOptionsList = this.internalOptionsList.slice();
        }
        this.externalOptionsQueue = undefined;
        this.onChangeFunction(null);
        this.change.emit(null);
    }

    protected handleExternalValueChanges(values: string[]) {
        this.selectedOptions = [];

        if (!this.internalOptionsList.length) {
            this.externalOptionsQueue = values;
            return;
        }

        for (let i = 0; i < values.length; i++) {
            const value = values[i].toString();
            const index = this.internalOptionsList.findIndex((item) => item.id.toString() === value);
            this.selectOption(this.internalOptionsList[index], false);
        }

        this.externalOptionsQueue = undefined;
        // this.changeDetectorRef.detectChanges();
    }

    public deselectOption(option: ListItem) {
        this.selectedOptions.splice(
            this.selectedOptions.findIndex((item) => item === option),
            1,
        );
        this.updateAvailableOptionsList();
        this.onChangeFunction(this.selectedOptions?.length === 0 ? null : this.selectedOptions.map((opt) => opt.id));
        this.change.emit(this.selectedOptions?.length === 0 ? null : this.selectedOptions.map((opt) => opt.id));
        this.externalOptionsQueue = undefined;
    }

    protected updateAvailableOptionsList() {
        this.computeMaximumSelectionStatus();
        this.filteredAvailableOptionsList = this.filteredOptionsList.filter(
            (option) => this.selectedOptions.findIndex((item) => item.id === option.id) === -1,
        );
    }

    protected addSelectionsFromQueue() {
        if (this.externalOptionsQueue) {
            const queueLength = this.externalOptionsQueue.length;

            for (let i = 0; i < queueLength; i++) {
                const value = this.externalOptionsQueue[i].toString();
                const index = this.internalOptionsList.findIndex((item) => item.id.toString() === value);
                this.selectOption(this.internalOptionsList[index], false);
            }
        }

        this.externalOptionsQueue = undefined;
    }

    protected areDynamicOptionsValid(): boolean {
        return (this._dynamicOptions && isFunction(this._dynamicOptions.getDataFunction)) as boolean;
    }

    protected createOptionsList(data: any[]) {
        this.internalOptionsList = data.map((item: any) =>
            typeof item === 'string' || typeof item === 'number'
                ? new ListItem(item)
                : new ListItem({
                      id: item[this.idKey],
                      label: item[this.labelKey],
                      unselectable: item.unselectable,
                  }),
        );
        this.filteredOptionsList = this.internalOptionsList.slice();
        this.filteredAvailableOptionsList = this.internalOptionsList.filter(
            (option) => this.selectedOptions.findIndex((item) => item.id === option.id) === -1,
        );
    }

    public isSelectedOptionFullyVisible(): boolean {
        //When no more elements are left in the list the checks below will generate a console error
        //since it cannot 'get' the child with index equal to 'selectedIndex'
        if (!this.optionsChildren?.length) {
            return true;
        }

        if (this.selectedIndex === -1) {
            this.selectedIndex = 0;
        }
        const currentElement = this.optionsChildren.get(this.selectedIndex)?.nativeElement;
        const elementParent = this.dropdownMenu.nativeElement;

        const elementTop = currentElement.offsetTop;
        const elementBottom = currentElement.offsetTop + currentElement.clientHeight;
        const parentTop = elementParent.scrollTop;
        const parentBottom = elementParent.scrollTop + elementParent.clientHeight;

        return parentTop < elementTop && parentBottom > elementBottom;
    }

    public openSelectedOptionsModal() {
        const modalData = { selectedOptions: clone(this.selectedOptions), type: 'center', enforceOneValue: this.enforceOneValue };
        const reference = this.modalService.openModal(MultiInputControlModalComponent, modalData);
        this.subscriptions.openSelectedOptionsModal = reference.onClose.subscribe((data) => {
            if (data) {
                this.selectedOptions = data;
                this.updateAvailableOptionsList();
                this.onChangeFunction(this.selectedOptions?.length === 0 ? null : this.selectedOptions.map((opt) => opt.id));
                this.change.emit(this.selectedOptions?.length === 0 ? null : this.selectedOptions.map((opt) => opt.id));
                this.externalOptionsQueue = undefined;
            }
        });
    }

    public moveUp() {
        if (this.isOptionsListVisible()) {
            this.selectedIndex = Math.max(this.selectedIndex - 1, 0);

            if (!this.isSelectedOptionFullyVisible()) {
                const selectedOption = this.optionsChildren.get(this.selectedIndex)?.nativeElement;
                selectedOption.scrollIntoView({ block: 'start' });
            }
        } else {
            this.openOptionsList();
        }
    }

    public moveDown() {
        if (this.isOptionsListVisible()) {
            this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredAvailableOptionsList.length - 1);

            if (!this.isSelectedOptionFullyVisible()) {
                const selectedOption = this.optionsChildren.get(this.selectedIndex)?.nativeElement;
                selectedOption.scrollIntoView({ block: 'end' });
            }
        } else {
            this.openOptionsList();
        }
    }

    public isOptionsListVisible() {
        return this.showDropdownCurrentValue;
    }

    public mouseMove(index: number) {
        this.selectedIndex = index;
    }

    public openOptionsList() {
        this.dropdownTopPositioning = false;
        this.showDropdownSubject.next(true);
        this.selectedIndex = -1;
        this.changeDetectorRef.detectChanges();
        this.dropdownTopPositioning = !isElementFullyVisible(this.dropdownMenu?.nativeElement);
        this.changeDetectorRef.markForCheck();
    }

    public isOptionAlreadyPresent(value: any) {
        return this.selectedOptions.find((option) => option.id === value);
    }

    public onSpaceKey(event: any) {
        if (!this.isOptionsListVisible()) {
            this.openOptionsList();
            event.preventDefault();
            event.stopPropagation();
        }
    }

    public closeOptionsList() {
        this.showDropdownSubject.next(false);
    }

    protected computeMaximumSelectionStatus() {
        if (!this.maximumSelections || this.maximumSelections < 1) {
            return;
        }

        if (this.selectedOptions.length >= this.maximumSelections) {
            this.form.get('searchInput')?.disable({ emitEvent: false });
        } else if (this.form.get('searchInput')?.status === 'DISABLED') {
            this.form.get('searchInput')?.enable({ emitEvent: false });
        }
    }
}
